import React from 'react';

/**
 * CircularProgress - SVG-based circular progress indicator
 * Used for profile completeness visualization
 * 
 * @param {number} value - Progress value (0-100)
 * @param {number} size - Circle diameter in pixels (default: 112)
 * @param {number} stroke - Stroke width in pixels (default: 10)
 * @param {string} label - Accessible label for screen readers
 * @param {string} className - Additional CSS classes
 */
export const CircularProgress = ({ 
  value = 0, 
  size = 112, 
  stroke = 10, 
  label,
  className = ''
}) => {
  // Calculate circle dimensions
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  
  // Clamp value between 0 and 100
  const clampedValue = Math.max(0, Math.min(100, value));
  
  return (
    <div className={`relative inline-flex items-center justify-center ${className}`} data-testid="progress-circle-container">
      <svg 
        width={size} 
        height={size} 
        role="img" 
        aria-label={label || `Profile completeness ${clampedValue}%`}
        data-testid="progress-circle-svg"
      >
        {/* Background circle (track) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="hsl(var(--muted))"
          strokeWidth={stroke}
          fill="none"
          data-testid="progress-circle-track"
        />
        
        {/* Progress circle (indicator) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="hsl(var(--primary))"
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 300ms ease',
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%'
          }}
          data-testid="progress-circle-indicator"
        />
        
        {/* Center text showing percentage */}
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          className="text-2xl font-bold font-heading"
          fill="currentColor"
          data-testid="progress-circle-text"
        >
          {clampedValue}%
        </text>
      </svg>
    </div>
  );
};

export default CircularProgress;
